import { nextTick, onMounted, Ref, watch } from 'vue'

import { StoreChatMessages } from '@shared/models/interfaces/messages'
import { IMessage } from '@shared/models/proto/base/messages'
import { Nullable } from '@shared/models/types/primary'
import { isGptBotPayload } from '@shared/utils/helpers/messages/bot-payload'
import { isNewMessageWithoutScroll } from '@shared/utils/helpers/messages/scroll-messages'

/**
 *
 * doc -  https://gitlab.com/qliqdev/qliqweb-vue/-/blob/dev/patient-chat/README.md?ref_type=heads#scroll-to-bottom-flow
 * */

export function useChatAutoScroll(
  chatContainer: Ref<Nullable<HTMLElement>>,
  chatMessages: Ref<StoreChatMessages>,
  watchCallback?: (scrollToBottom: () => void) => void
) {
  // Scroll to the bottom when new messages arrive
  watch(
    chatMessages,
    (nextMessages, prevMessages) => {
      const lastMessageIsGpt = isGptBotPayload(
        nextMessages.data?.data[0] as IMessage
      )

      if (lastMessageIsGpt) {
        return
      }

      // Do scroll if new message but don't scroll if user scrolls to next pagination page
      if (isNewMessageWithoutScroll(nextMessages, prevMessages)) {
        if (watchCallback) {
          watchCallback(scrollToBottom)
        }

        scrollToBottom()
      }
    },
    {
      deep: true,
    }
  )

  const scrollToBottom = async () => {
    await nextTick()

    if (chatContainer.value) {
      /**
       * @description for old browser
       * */
      if (typeof chatContainer.value.scrollTo === 'function') {
        const scrollOptions = {
          top: chatContainer.value.scrollHeight,
          behavior: 'smooth' as ScrollBehavior,
        }
        chatContainer.value?.scrollTo(scrollOptions)
      } else {
        chatContainer.value.scrollTop = chatContainer.value.scrollHeight
      }
    }
  }

  const scrollIfBottom = () => {
    if (isScrolledBottom()) {
      scrollToBottom()
    }
  }

  const isScrolledBottom = () => {
    const differ =
      (chatContainer.value?.scrollHeight ?? 0) -
      ((chatContainer.value?.scrollTop ?? 0) +
        (chatContainer.value?.offsetHeight ?? 0))

    const approximateMessageHeight = 40

    return chatContainer.value && differ < approximateMessageHeight
  }

  onMounted(() => {
    scrollToBottom()
  })

  return {
    isScrolledBottom,
    scrollToBottom,
    scrollIfBottom,
  }
}
